var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AmxBeaconListener } from './framework/AmxBeaconListener';
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { Timeouts } from './framework/Timeouts';
export const moduleTypeInfo = {
    ethernet: { type: 'ethernet' },
    ir: { type: 'ir' },
    ir_in: { type: 'ir', subType: 'in' },
    ir_out: { type: 'ir', subType: 'out' },
    irblaster: { type: 'irBlaster' },
    irtriport: { type: 'irTriPort' },
    irtriportblaster: { type: 'irTriPortBlaster' },
    module: { type: 'module' },
    relay: { type: 'relay' },
    relay_spst_3a: { type: 'relay', subType: 'spst-3a' },
    relaysensor: { type: 'relaySensor' },
    sensor_digital: { type: 'sensor', subType: 'digital' },
    serial: { type: 'serial' },
    serial_rs232: { type: 'serial', subType: 'rs232' },
    'switch_hdmi_3:1': { type: 'switch', subType: 'hdmi-3-1' },
    wifi: { type: 'wifi' },
};
function isGlobalCacheContactClosureConfig(config) {
    return 'moduleAddress' in config && 'port' in config;
}
export class GlobalCache {
    constructor(socket, state, log) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        // other class properties and constructor
        this.modules = [];
        this.timeouts = new Timeouts();
        this.versionNumber = '';
        this.nameInterval = this.timeouts.createInterval();
        // controls
        this.contactClosure = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const fullAddress = this.getFullAddress(key);
                if (fullAddress === undefined) {
                    this.log.error(`invalid key: ${key}`);
                    return false;
                }
                const match = yield this.socket.send(`getstate,${fullAddress.module}:${fullAddress.port}`, new RegExp(`^state,${fullAddress.module}:${fullAddress.port},(\\d+)`), tags);
                return match[1] !== '0';
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const fullAddress = this.getFullAddress(key);
                if (fullAddress === undefined) {
                    this.log.error(`invalid key: ${key}`);
                    return false;
                }
                const match = yield this.socket.send(`setstate,${fullAddress.module}:${fullAddress.port},${value ? '1' : '0'}`, new RegExp(`^(?:set)?state,${fullAddress.module}:${fullAddress.port},(\\d+)`), tags);
                return match[1] !== '0';
            }),
            getKey: (config) => {
                if (!isGlobalCacheContactClosureConfig(config)) {
                    throw new Error(`invalid contact closure config: ${JSON.stringify(config)}`);
                }
                return `relay_${config.moduleAddress}_${config.port}`;
            },
            validate: (config) => __awaiter(this, void 0, void 0, function* () {
                const result = {
                    moduleAddress: 'missing',
                    port: 'missing',
                };
                if (!isGlobalCacheContactClosureConfig(config)) {
                    this.log.error(`invalid contact closure config: ${JSON.stringify(config)}`);
                    return result;
                }
                for (const module of this.modules) {
                    if (module.address === config.moduleAddress) {
                        if (module.type === 'relay' || module.type === 'relaySensor') {
                            result.moduleAddress = 'ok';
                            if (config.port >= 1 && config.port <= module.portCount) {
                                result.port = 'ok';
                            }
                            else {
                                result.port = 'range';
                            }
                        }
                        else {
                            result.moduleAddress = 'incompatible';
                            result.port = 'incompatible';
                        }
                        break;
                    }
                }
                return result;
            }),
        };
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return {
                    channels: this.modules.map(module => (Object.assign(Object.assign(Object.assign(Object.assign({}, (module.type === 'relay' || module.type === 'relaySensor'
                        ? { filters: { contactClosures: true } }
                        : undefined)), { moduleAddress: module.address, port: { max: module.portCount, min: module.portCount > 0 ? 1 : 0 } }), (module.subType ? { subType: module.subType } : undefined)), { type: module.type }))),
                    videoInputs: this.modules
                        .filter(module => module.type === 'switch')
                        .flatMap(module => {
                        return Array.from({ length: module.portCount }, (_, i) => ({
                            code: `hdmiin_${module.address}_${i + 1}`,
                            external: true,
                            name: `HDMI In ${module.address}:${i + 1}`,
                        }));
                    }),
                    videoOutputs: this.modules
                        .filter(module => module.type === 'switch')
                        .map(module => ({
                        code: `hdmiout_${module.address}`,
                        name: `Module ${module.address}`,
                    })),
                };
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                // Flex WiFi response: 710-2000-20
                // Flex Relay response: 710-3000-19
                // GC-100-12 response: version,0,3.2-12
                // IP2CC response: 710-1009-05
                const match = yield this.socket.send(`getversion`, /(\d+\-\d+\-\d+)|version,\d+,(.+)/, tags);
                return (_a = match[1]) !== null && _a !== void 0 ? _a : match[2];
            }),
        };
        this.name = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.name.value;
            }),
        };
        this.videoMatrixRoutes = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                var _b;
                const moduleAddress = parseInt((_b = key.split('_')[1]) !== null && _b !== void 0 ? _b : '', 10);
                for (const module of this.modules) {
                    if (module.type !== 'switch' || module.address !== moduleAddress)
                        continue;
                    for (let i = 1; i <= module.portCount; i++) {
                        const match = yield this.socket.send(`getstate,${moduleAddress}:${i}`, /state,1:\d+,(\d)/i, tags);
                        if (match[1] === '1')
                            return `hdmiin_${moduleAddress}_${i}`;
                    }
                }
                throw new Error(`no video input selected for key = ${key} in modules = ${JSON.stringify(this.modules, null, 4)}`);
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _c;
                const moduleAddress = parseInt((_c = key.split('_')[1]) !== null && _c !== void 0 ? _c : '', 10);
                const input = this.getFullAddress(value);
                if (input === undefined)
                    throw new Error(`invalid input key: ${value}`);
                if (moduleAddress !== input.module) {
                    this.log.warning(`can't switch inputs on other modules`);
                    return this.state.videoMatrixRoutes.values[key];
                }
                const match = yield this.socket.send(`setstate,1:${input.port},1`, /state,1:\d+,(1|0)|ERR/i, tags);
                return match[1] === '1' ? value : this.state.videoMatrixRoutes.values[key];
            }),
            hasControl: (config) => __awaiter(this, void 0, void 0, function* () {
                const hasHdmiModule = this.modules.find(m => m.type === 'switch') !== undefined;
                const hasVideoInputsConfig = config.videoInputs !== undefined;
                const hasVideoMatrixRoutes = hasHdmiModule && hasVideoInputsConfig;
                return hasVideoMatrixRoutes;
            }),
        };
        // polling
        this.polls = [
            // Poll firmware version too in case no contact closures or video matrix routes are devined.
            { intervalMs: 4000, controls: ['contactClosure', 'firmwareVersion', 'videoMatrixRoutes'] },
        ];
    }
    static discoverEquipment() {
        var _a, _b, _c, _d, _e;
        return __awaiter(this, void 0, void 0, function* () {
            // Captured AMX beacons for Global Cache devices:
            // AMXB<-UUID=GlobalCache_000C1E04363B><-SDKClass=Utility><-Make=GlobalCache><-Model=iTachIP2CC><-Revision=710-1008-05><-Pkg_Level=GCPK001><-Config-URL=http://192.168.2.206><-PCB_PN=025-0029-02><-Status=Ready>
            // AMXB<-UUID=GlobalCache_000C1E03B988><-SDKClass=Utility><-Make=GlobalCache><-Model=iTachIP2IR><-Revision=710-1005-05><-Pkg_Level=GCPK002><-Config-URL=http://192.168.2.134><-PCB_PN=025-0028-03><-Status=Ready>
            // AMXB<-UUID=GlobalCache_000C1E051C16><-SDKClass=Utility><-Make=GlobalCache><-Model=iTachIP2SL><-Revision=710-1009-05><-Pkg_Level=GCPK001><-Config-URL=http://192.168.2.217><-PCB_PN=025-0027-11><-Status=Ready>
            const beacons = AmxBeaconListener.getBeacons();
            const results = [];
            const macAddressRegExp = /GlobalCache_([0-9a-fA-F]+)/;
            const ipAddressRegExp = /(\d+\.\d+\.\d+\.\d+)/;
            for (const beacon of beacons) {
                if (beacon.props.Make !== 'GlobalCache')
                    continue;
                const macAddress = (_b = macAddressRegExp.exec((_a = beacon.props.UUID) !== null && _a !== void 0 ? _a : '')) === null || _b === void 0 ? void 0 : _b[1];
                if (!macAddress)
                    continue;
                const modelName = (_c = beacon.props.Model) !== null && _c !== void 0 ? _c : 'Unknown';
                const ipAddress = (_e = ipAddressRegExp.exec((_d = beacon.props['Config-URL']) !== null && _d !== void 0 ? _d : '')) === null || _e === void 0 ? void 0 : _e[1];
                if (!ipAddress)
                    continue;
                results.push({
                    equipmentTypeId: GlobalCache.key,
                    ipAddress,
                    macAddress,
                    modelName,
                    type: 'globalCache',
                });
            }
            let id = 0;
            return Object.fromEntries(results.map(result => ['a' + (id++).toString(), result]));
        });
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            const { name, foundInBeacon } = this.getName();
            this.state.name.update(name);
            // If the name wasn't found in the AMX beacon, then maybe we haven't received it yet. Set
            // an interval timer to keep checking until we find it.
            if (!foundInBeacon) {
                this.nameInterval.set(() => {
                    const { name, foundInBeacon } = this.getName();
                    if (foundInBeacon) {
                        this.state.name.update(name);
                        this.nameInterval.clear();
                    }
                }, 10000);
            }
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield CommandSocketInitializer.initialize(this.socket, [
                {
                    errorCode: 'verifyFailed',
                    send: 'get_NET,0:1',
                    succeed: /^NET,0:1,.+/,
                },
            ], credentials);
            // Get the modules here rather than in detected.get because we need them for hasControl.
            if (result === 'connected') {
                this.modules.length = 0;
                // Also work around documented bugs in Flex responses by detecting the model number and
                // hard-coding the ports for that device.
                const tags = ['initialize'];
                const match = yield this.socket.send(`getversion`, /(?:version,\d+,)?(.+)/, tags);
                this.versionNumber = match[1];
                const isFlex = this.versionNumber.startsWith('710-3000-') ||
                    this.versionNumber.startsWith('710-2000');
                const id = this.socket.registerAsync(/device,(\d+),(\d+)\s+(\S+)/, match => {
                    const address = parseInt(match[1], 10);
                    let portCount = isFlex ? 4 : parseInt(match[2], 10);
                    const typeInfo = moduleTypeInfo[match[3].toLowerCase()];
                    if (typeInfo === undefined) {
                        this.log.error(`unrecognized module type ${match[3]}`);
                    }
                    else {
                        // Ethernet port count is reported as 0, but is actually 1.
                        if (typeInfo.type === 'ethernet')
                            portCount = 1;
                        this.modules.push(Object.assign({ address,
                            portCount, type: typeInfo.type }, (typeInfo.subType ? { subType: typeInfo.subType } : undefined)));
                    }
                });
                try {
                    yield this.socket.send(`getdevices`, /^endlistdevices/, tags);
                }
                finally {
                    this.socket.unregisterAsync(id);
                }
            }
            return result;
        });
    }
    // other class methods
    getFullAddress(key) {
        const parts = key.split('_');
        const module = parseInt(parts[1], 10);
        const port = parts[2] === undefined ? undefined : parseInt(parts[2], 10);
        if (module === undefined || port === undefined)
            return undefined;
        return { module, port };
    }
    getName() {
        // First, try to look up our version number in the AMX beacons. If it is there, use the
        // model reported in the AMX beacon.
        const beacons = AmxBeaconListener.getBeacons();
        const beacon = beacons.find(beacon => {
            var _a;
            return ((_a = beacon.props.UUID) === null || _a === void 0 ? void 0 : _a.startsWith('GlobalCache')) &&
                beacon.props.Revision === this.versionNumber;
        });
        // If we haven't received an AMX beacon, then set the name based on the version number
        // information from the API guide.
        if (beacon !== undefined && beacon.props.Model !== undefined) {
            return { name: beacon.props.Model, foundInBeacon: true };
        }
        if (this.versionNumber === '3.2-06') {
            return { name: 'GC-100-06', foundInBeacon: false };
        }
        else if (this.versionNumber === '3.2-12') {
            return { name: 'GC-100-12', foundInBeacon: false };
        }
        else if (this.versionNumber.startsWith('710-1005')) {
            return { name: 'iTachIP2IR', foundInBeacon: false };
        }
        else if (this.versionNumber.startsWith('710-1009')) {
            return { name: 'iTachIP2SL', foundInBeacon: false };
        }
        else if (this.versionNumber.startsWith('710-1008')) {
            return { name: 'iTachIP2CC', foundInBeacon: false };
        }
        else if (this.versionNumber.startsWith('710-1001')) {
            return { name: 'iTachWF2IR', foundInBeacon: false };
        }
        else if (this.versionNumber.startsWith('710-1007')) {
            return { name: 'iTachWF2SL', foundInBeacon: false };
        }
        else if (this.versionNumber.startsWith('710-1010')) {
            return { name: 'iTachWF2CC', foundInBeacon: false };
        }
        else if (this.versionNumber.startsWith('710-2000')) {
            return { name: 'FlexWF', foundInBeacon: false };
        }
        else if (this.versionNumber.startsWith('710-3000')) {
            return { name: 'FlexIP', foundInBeacon: false };
        }
        else if (this.versionNumber.startsWith('710-4001')) {
            return { name: 'GlobalConnectIR', foundInBeacon: false };
        }
        else if (this.versionNumber.startsWith('710-4002')) {
            return { name: 'GlobalConnectSL', foundInBeacon: false };
        }
        else if (this.versionNumber.startsWith('710-4003')) {
            return { name: 'GlobalConnectRL', foundInBeacon: false };
        }
        else if (this.versionNumber.startsWith('710-4004')) {
            return { name: 'GlobalConnectSW', foundInBeacon: false };
        }
        return { name: 'Unknown Model', foundInBeacon: false };
    }
}
// static properties
GlobalCache.driverType = 'socketDriver';
GlobalCache.key = '-MqK-Sgk2f5J28Rwk_c1';
GlobalCache.socketOptions = [{ port: 4998 }];
